/**
 * 竞价投放预加载缓存：每日首次进入系统时在后台预拉取经理户/汇总/广告主/账户列表，
 * 用户进入竞价投放页时优先使用缓存以加快首屏展示。
 */
import {
  getManagerList,
  getSnapshotSummary,
  getSnapshotAdvertisers,
  getAccountList
} from '@/api/bidding'

function getTodayDateKey() {
  return new Date().toISOString().slice(0, 10)
}

/** 预加载 live 数据在此时长内视为新鲜，竞价页跳过重复的 summary/accounts 后台刷新 */
export const PRELOAD_LIVE_FRESH_MS = 60 * 1000

export default {
  namespaced: true,
  state: {
    managerList: null,
    summary: null,
    advertiserList: null,
    accountList: null,
    accountTotal: null,
    managerId: null,
    dateKey: null,
    platformType: null,
    /** 预加载完成时间（ms），用于短窗口内跳过页内重复 live 请求 */
    fetchedAt: null,
    loading: false,
    error: null
  },
  getters: {
    /** 是否有可用的预加载数据（与当前经理户、日期匹配时可复用） */
    hasPreload(state) {
      return state.managerList != null && state.managerList.length > 0
    },
    preloadForManagerAndDate: (state) => (managerId, dateKey) => {
      if (!state.managerId || !state.dateKey) return null
      if (String(state.managerId) !== String(managerId)) return null
      if (state.dateKey !== dateKey) return null
      return {
        summary: state.summary,
        advertiserList: state.advertiserList,
        accountList: state.accountList,
        accountTotal: state.accountTotal,
        platformType: state.platformType,
        fetchedAt: state.fetchedAt
      }
    },
    /** 预加载完成后短时内：经理户/日期/平台一致则跳过页内重复 live summary/accounts */
    isLiveDataFresh: (state) => (managerId, dateKey, platformType, maxAgeMs = PRELOAD_LIVE_FRESH_MS) => {
      if (!state.fetchedAt || !maxAgeMs) return false
      if (Date.now() - state.fetchedAt > maxAgeMs) return false
      if (!state.managerId || !state.dateKey) return false
      if (String(state.managerId) !== String(managerId)) return false
      if (state.dateKey !== dateKey) return false
      if (String(state.platformType || '') !== String(platformType || '')) return false
      return true
    }
  },
  mutations: {
    setPreload(state, payload) {
      state.managerList = payload.managerList != null ? payload.managerList : state.managerList
      state.summary = payload.summary != null ? payload.summary : state.summary
      state.advertiserList = payload.advertiserList != null ? payload.advertiserList : state.advertiserList
      state.accountList = payload.accountList != null ? payload.accountList : state.accountList
      state.accountTotal = payload.accountTotal != null ? payload.accountTotal : state.accountTotal
      state.managerId = payload.managerId != null ? payload.managerId : state.managerId
      state.dateKey = payload.dateKey != null ? payload.dateKey : state.dateKey
      state.platformType = payload.platformType != null ? payload.platformType : state.platformType
      if (payload.fetchedAt != null) {
        state.fetchedAt = payload.fetchedAt
      } else if (payload.managerId != null || payload.accountList != null || payload.summary != null) {
        state.fetchedAt = Date.now()
      }
      state.loading = false
      state.error = null
    },
    setPreloadLoading(state, loading) {
      state.loading = loading
    },
    setPreloadError(state, err) {
      state.loading = false
      state.error = err
    },
    clearPreload(state) {
      state.managerList = null
      state.summary = null
      state.advertiserList = null
      state.accountList = null
      state.accountTotal = null
      state.managerId = null
      state.dateKey = null
      state.platformType = null
      state.fetchedAt = null
      state.loading = false
      state.error = null
    }
  },
  actions: {
    /** 后台预加载：经理户列表 → 第一个经理户的今日汇总、广告主、账户第一页 */
    async runPreload({ commit }, payload = {}) {
      const platformType = (payload && payload.platformType) ? String(payload.platformType) : 'jh'
      commit('setPreloadLoading', true)
      const dateKey = getTodayDateKey()
      try {
        const managersRes = await getManagerList({ platformType })
        const managerList = (managersRes && managersRes.list) ? managersRes.list : []
        if (managerList.length === 0) {
          commit('setPreload', { managerList: [], managerId: null, dateKey })
          return
        }
        const first = managerList[0]
        const managerId = first.managerId != null ? String(first.managerId) : null
        if (!managerId) {
          commit('setPreload', { managerList, managerId: null, dateKey })
          return
        }
        const baseParams = {
          managerAccountId: managerId,
          advertiserId: 'all',
          startDate: dateKey,
          endDate: dateKey,
          platformType
        }
        const [summaryRes, advertisersRes, accountRes] = await Promise.all([
          getSnapshotSummary(baseParams).catch(() => null),
          getSnapshotAdvertisers(baseParams).catch(() => null),
          getAccountList({ ...baseParams, page: 1, pageSize: 50 }).catch(() => null)
        ])
        let summary = null
        if (summaryRes && (summaryRes.snapshotAt != null || summaryRes.cost != null || summaryRes.todayConsume != null)) {
          summary = {
            todayConsume: summaryRes.todayConsume ?? null,
            todayAccountWithConsume: summaryRes.todayAccountWithConsume ?? null,
            insufficientBalanceCount: summaryRes.insufficientBalanceCount ?? null,
            cost: summaryRes.cost ?? null,
            impressions: summaryRes.impressions ?? null,
            clicks: summaryRes.clicks ?? null,
            avgClickPrice: summaryRes.avgClickPrice ?? null,
            billingRatio: summaryRes.billingRatio ?? null,
            iaa: summaryRes.iaa ?? null,
            actualConversionCost: summaryRes.actualConversionCost ?? null,
            advertisersQueried: summaryRes.advertisersQueried ?? null,
            fallbackToManager: summaryRes.fallbackToManager === true
          }
        }
        const advertiserList = (advertisersRes && advertisersRes.list)
          ? advertisersRes.list.map(item => ({ ...item, advertiserId: item.advertiserId != null ? String(item.advertiserId) : '' }))
          : null
        const accountList = (accountRes && accountRes.list) ? accountRes.list : null
        const accountTotal = accountRes && accountRes.total != null ? accountRes.total : null
        commit('setPreload', {
          managerList,
          summary,
          advertiserList,
          accountList,
          accountTotal,
          managerId,
          dateKey,
          platformType,
          fetchedAt: Date.now()
        })
      } catch (e) {
        commit('setPreloadError', e && e.message ? e.message : '预加载失败')
      }
    }
  }
}

export { getTodayDateKey }
